import { ChangeDetectorRef, Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { Application, ProfileConfig, SelfService, SelfServiceComponent } from 'sigesr-self-service-lib';
import { environment } from '../../environments/environment';
import { KeycloakAuthService } from '../services/keycloak-auth.service';
import { PortalApp } from '../app.interface';
import { AppService } from '../services/app';

@Component({
  selector: 'home-sigesr',
  standalone: true,
  imports: [CommonModule, RouterModule, SelfServiceComponent],
  templateUrl: './home-sigesr.html',
  styleUrls: ['./home-sigesr.scss']
})
export class HomeSigesrComponent implements OnInit {
  allAppsWithStatus: (PortalApp & { disabled: boolean })[] = [];
  userName = '';
  userEmail = '';
  userRole = '';
  lastConnection = '';
  accessDeniedApp: string | null = null;
  isLoading = true;
  isLoadingUser = true;
  isAuthChecking = true;
  currentApp: Application | null = null;
  currentUser: any = null;

  profileConfig: ProfileConfig = {
    showProfil: true,
    showChangePassword: true,
    avatarBackgroundColor: '#45B56F',
    showLogout: true,
    showAppSelector: false,
    maxVisibleApps: 9,
    appBuilder: {
      currentApp: environment.keycloak.clientId,
      environment: environment.env as 'dev' | 'prod' | 'staging' | 'uat'
    }
  };

  constructor(
    private keycloakAuth: KeycloakAuthService,
    private appService: AppService,
    private route: ActivatedRoute,
    private selfService: SelfService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  async ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      const isLoggedIn = await this.keycloakAuth.isLoggedIn();
      if (!isLoggedIn) {
        this.keycloakAuth.login();
        return;
      }

      this.isAuthChecking = false;
      await Promise.all([this.loadUserInfo(), this.loadAllAppsWithAccess()]);
      this.checkAccessDenied();
    } catch (error) {
      console.error('[Home] Erreur init:', error);
      this.isAuthChecking = false;
    } finally {
      this.isLoading = false;
      this.isLoadingUser = false;
      this.cdr.detectChanges();
    }
  }

  private async loadUserInfo() {
    try {
      const keycloak = this.keycloakAuth.getInstance();
      if (keycloak && keycloak.authenticated) {
        const profile = await keycloak.loadUserInfo();

        const firstName = profile?.['given_name'] || profile?.['firstName'] || '';
        const lastName = profile?.['family_name'] || profile?.['lastName'] || '';
        const fullName = `${firstName} ${lastName}`.trim();

        this.userName = fullName
          || profile?.['name']
          || profile?.['preferred_username']
          || profile?.['username']
          || 'Utilisateur inconnu';

        this.userEmail = profile?.['email'] || 'Email non disponible';
        this.lastConnection = this.formatLastConnection();

        this.currentUser = {
          ...profile,
          username: profile?.['preferred_username'] || profile?.['username'],
          email: profile?.['email'],
          lastConnection: this.lastConnection,
          fullname: this.userName
        };

        this.selfService.setUserProfile(this.currentUser);
      } else {
        this.currentUser = {
          username: 'Invité',
          email: '',
          firstname: 'Utilisateur',
          lastname: 'Anonyme',
        };
        this.selfService.setUserProfile(this.currentUser);
      }
    } catch (error) {
      console.error('[Home] Erreur chargement utilisateur', error);
      this.currentUser = { fullname: 'Utilisateur', lastConnection: this.formatLastConnection() };
      this.userName = 'Utilisateur';
      this.userEmail = 'Email non disponible';
      this.userRole = 'Utilisateur';
      this.lastConnection = this.formatLastConnection();
    }
  }

  private formatLastConnection(): string {
    return new Date().toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }

  private checkAccessDenied() {
    this.route.queryParams.subscribe(params => {
      if (params['access_denied']) {
        this.accessDeniedApp = params['app'] || 'une application';
        setTimeout(() => window.history.replaceState({}, '', window.location.pathname), 100);
      }
    });
  }

  private async loadAllAppsWithAccess() {
    try {
      this.isLoading = true;
      const accessibleApps = await this.appService.getAccessibleApps();
      const accessibleClientIds = new Set(accessibleApps.map(app => app.clientId));
      const allApps = this.appService.getAllApps();

      this.allAppsWithStatus = allApps.map(app => ({
        ...app,
        disabled: !accessibleClientIds.has(app.clientId)
      }));
      this.allAppsWithStatus.sort((a, b) => (a.disabled === b.disabled ? 0 : a.disabled ? 1 : -1));
    } catch (error) {
      console.error('[Home] Erreur chargement apps:', error);
      const allApps = this.appService.getAllApps();
      this.allAppsWithStatus = allApps.map(app => ({ ...app, disabled: true }));
    }
  }

  handleAppSelected(app: Application) {
    this.currentApp = app;
    this.selfService.setCurrentApp(app);
    this.profileConfig.currentAppId = app.id;
    if (app.url) this.router.navigate([app.url]);
  }

  handleChangePassword(): void {
    alert('Ouverture du formulaire de changement de mot de passe...');
  }

  handleLogout(): void {
    if (confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
      localStorage.clear();
      this.keycloakAuth.logout(window.location.origin);
    }
  }

  handleShowAllApps() { }
}